import { useEffect, useRef, useState, type ReactNode } from 'react'
import { ChevronLeft, ChevronRight, Download, ExternalLink } from 'lucide-react'
import { Modal, ModalContent, ModalHeader, ModalTitle } from '@/components/ui/Modal'
import { formatBytes } from '@/utils/formatBytes'
import type { ViewableKind } from '@/utils/mediaView'
import { followDownload, useMediaDownload, usePlayableLink } from './mediaLink'
import { MEDIA_TEXT, kindLabel, ofCount } from './mediaViewText'

export { RENEW_MARGIN_MS } from './mediaLink'

/** SA-1094: one of a message's viewable attachments, as the viewer sees it. */
export interface MediaViewerItem {
  kind: ViewableKind
  messageId: string
  mediaUrl: string
  filename?: string | null
  byteSize?: number | null
  /** Bytes already loaded in the thread (a small image): shown as they are, no link minted. */
  objectUrl?: string | null
}

/** SA-1331: a file the page already holds as a blob (a record's quotation): shown as it is,
 *  no link minted, no new tab; Download saves the blob under `filename` without a request. */
export interface BlobViewerItem {
  kind: ViewableKind
  blobUrl: string
  filename: string
  byteSize?: number | null
}

/** What the thread's bubbles hand the viewer: every caller's shape, unchanged. */
export type ViewerItem = MediaViewerItem

/** What the dialog itself shows: a message's file or a blob the page holds. */
export type DialogItem = MediaViewerItem | BlobViewerItem

/** SA-1094: the set a bubble hands each of its attachments, and which one it is. */
export interface ViewerSet {
  items: readonly ViewerItem[]
  index: number
}

interface MediaViewerDialogProps {
  items: readonly DialogItem[]
  index: number
  onClose: () => void
}

/** The elements whose own arrow keys win over the dialog's stepping. */
const KEEPS_ARROWS = new Set(['VIDEO', 'AUDIO', 'IFRAME'])

/**
 * SA-981 (design note §4.2): one dialog for every viewable kind on every
 * channel: a PDF in the browser's own viewer in a frame, a video or audio
 * file in a player, an image at full size. The view link is minted when the
 * dialog opens and dropped when it closes. Render it only while open.
 * SA-1094: it opens on a set of a message's attachments and steps through
 * them (buttons, arrow keys); a set of one looks as it always did.
 */
export function MediaViewerDialog({ items, index, onClose }: MediaViewerDialogProps) {
  // SA-1110: an empty set shows nothing, and an index past the set opens
  // its last item; every step stays inside the set.
  if (items.length === 0) return null
  return <ViewerBody items={items} startIndex={Math.min(Math.max(index, 0), items.length - 1)} onClose={onClose} />
}

function ViewerBody({ items, startIndex, onClose }: { items: readonly DialogItem[]; startIndex: number; onClose: () => void }) {
  const [index, setIndex] = useState(startIndex)
  const count = items.length
  const at = Math.min(index, count - 1)
  const item = items[at]
  const blob = 'blobUrl' in item
  const { kind, filename, byteSize } = item
  // A blob item has no message: its `''` reaches `useMediaDownload`, whose `download` is never called for it,
  // because its Download follows the blob through `followDownload` (SA-1333).
  const messageId = blob ? '' : item.messageId
  const mediaUrl = blob ? item.blobUrl : item.mediaUrl
  const objectUrl = blob ? item.blobUrl : (item.objectUrl ?? null)
  const canPrev = at > 0
  const canNext = at < count - 1
  const { src, link, status, renewals, retry, onError, onLoadedMetadata } = usePlayableLink(mediaUrl, { objectUrl })
  const { state: downloadState, download } = useMediaDownload(messageId, mediaUrl, filename)
  const title = filename || kindLabel(kind)

  const prevRef = useRef<HTMLButtonElement>(null)
  const nextRef = useRef<HTMLButtonElement>(null)

  // After a step, a pressed button that is now disabled would drop the focus
  // to the body, outside the dialog: hand it to the other step button.
  useEffect(() => {
    const active = document.activeElement
    if (active === prevRef.current && !canPrev) nextRef.current?.focus()
    else if (active === nextRef.current && !canNext) prevRef.current?.focus()
  }, [canPrev, canNext])

  // The arrows step wherever the focus is inside the dialog, except in the
  // player or the frame, which keep their own arrows. Escape stays Radix's.
  useEffect(() => {
    if (count < 2) return
    function onKeyDown(e: KeyboardEvent) {
      if (KEEPS_ARROWS.has((e.target as HTMLElement | null)?.tagName ?? '')) return
      if (e.key === 'ArrowLeft' && canPrev) {
        e.preventDefault()
        setIndex(at - 1)
      } else if (e.key === 'ArrowRight' && canNext) {
        e.preventDefault()
        setIndex(at + 1)
      }
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [count, canPrev, canNext, at])

  let body: ReactNode = null
  if (status === 'opening') {
    body = <p className="m-auto text-sm text-muted-foreground">{MEDIA_TEXT.opening}</p>
  } else if (status === 'gone') {
    body = <p className="m-auto text-sm text-muted-foreground">{MEDIA_TEXT.gone}</p>
  } else if (status === 'failed') {
    body = (
      <div className="m-auto flex flex-col items-center gap-2">
        <p className="text-sm text-destructive">{MEDIA_TEXT.failed}</p>
        <button
          type="button"
          onClick={retry}
          className="text-sm text-primary underline"
        >
          {MEDIA_TEXT.tryAgain}
        </button>
      </div>
    )
  } else if (status === 'unplayable') {
    body = (
      <p className="m-auto text-sm text-muted-foreground">
        {kind === 'image' ? MEDIA_TEXT.cannotShowImage : MEDIA_TEXT.cannotPlay}
      </p>
    )
  } else if (src && kind === 'pdf') {
    body = <iframe src={src} title={title} className="m-auto w-full h-full rounded-sm border border-border bg-white" />
  } else if (src && kind === 'video') {
    body = (
      <video
        src={src}
        controls
        autoPlay={renewals === 0}
        playsInline
        data-testid="media-viewer-video"
        className="m-auto max-w-full max-h-full"
        onError={e => { void onError(e.currentTarget) }}
        onLoadedMetadata={e => onLoadedMetadata(e.currentTarget)}
      />
    )
  } else if (src && kind === 'audio') {
    body = (
      <audio
        src={src}
        controls
        autoPlay={renewals === 0}
        data-testid="media-viewer-audio"
        className="m-auto w-full max-w-xl"
        onError={e => { void onError(e.currentTarget) }}
        onLoadedMetadata={e => onLoadedMetadata(e.currentTarget)}
      />
    )
  } else if (src && kind === 'image') {
    body = (
      <img
        src={src}
        alt={title}
        className="m-auto max-w-full"
        onError={() => { void onError(null) }}
      />
    )
  }

  return (
    <Modal open onOpenChange={open => { if (!open) onClose() }}>
      <ModalContent size="full" className="w-[90vw] h-[85vh] flex flex-col">
        <div className="flex-1 min-h-0 flex flex-col">
          <ModalHeader className="pr-14 flex flex-wrap items-center gap-x-3 gap-y-1">
            <ModalTitle className="text-base min-w-0 [overflow-wrap:anywhere]">{title}</ModalTitle>
            {byteSize != null && (
              <span className="text-xs text-muted-foreground">{formatBytes(byteSize)}</span>
            )}
            {count > 1 && (
              <span className="text-xs text-muted-foreground" aria-live="polite">{ofCount(at + 1, count)}</span>
            )}
            <div className="ml-auto flex items-center gap-3">
              {count > 1 && (
                <>
                  <button
                    type="button"
                    ref={prevRef}
                    onClick={() => setIndex(at - 1)}
                    disabled={!canPrev}
                    aria-label={MEDIA_TEXT.previous}
                    className="text-primary disabled:opacity-40"
                  >
                    <ChevronLeft className="w-4 h-4" aria-hidden="true" />
                  </button>
                  <button
                    type="button"
                    ref={nextRef}
                    onClick={() => setIndex(at + 1)}
                    disabled={!canNext}
                    aria-label={MEDIA_TEXT.next}
                    className="text-primary disabled:opacity-40"
                  >
                    <ChevronRight className="w-4 h-4" aria-hidden="true" />
                  </button>
                </>
              )}
              {link && !objectUrl && (
                <a
                  href={link.url}
                  target="_blank"
                  rel="noopener"
                  className="text-xs text-primary underline flex items-center gap-1"
                >
                  <ExternalLink className="w-3 h-3" aria-hidden="true" />
                  {MEDIA_TEXT.openInNewTab}
                </a>
              )}
              <button
                type="button"
                onClick={() => { if (blob) followDownload(mediaUrl, filename ?? ''); else void download() }}
                disabled={downloadState === 'preparing'}
                className="text-xs text-primary underline flex items-center gap-1 disabled:opacity-60"
              >
                <Download className="w-3 h-3" aria-hidden="true" />
                {downloadState === 'preparing' ? MEDIA_TEXT.preparingDownload : MEDIA_TEXT.download}
              </button>
            </div>
            {downloadState === 'gone' && (
              <p className="basis-full text-xs text-muted-foreground italic">{MEDIA_TEXT.gone}</p>
            )}
            {downloadState === 'failed' && (
              <p className="basis-full text-xs text-destructive italic">{MEDIA_TEXT.downloadFailed}</p>
            )}
          </ModalHeader>
          <div className="flex-1 min-h-0 overflow-auto p-4 flex">
            {body}
          </div>
        </div>
      </ModalContent>
    </Modal>
  )
}
